/**
 * LiveTranscript — appears while the council WebSocket is streaming,
 * renders each persona's thinking → done transition, and disappears
 * once the saved record arrives.
 */
import React from 'react';
import { Loader2, Check, Sparkles } from 'lucide-react';
import { ICON_MAP } from './_constants';

export default function LiveTranscript({ liveStage, liveAnswers, personas, moderator }) {
  const moderatorName = personas.find((p) => p.id === moderator)?.english_name || moderator;
  const headline =
    liveStage === 'moderating' ? `${moderatorName} is synthesising…`
    : liveStage.startsWith('round_') ? `Live · round ${liveStage.replace('round_', '')}`
    : 'Convening…';

  return (
    <div className="mt-4 ve-card p-5" data-testid="council-live-transcript">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5] inline-flex items-center gap-2">
          <Loader2 size={12} className="animate-spin" />
          {headline}
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-white/45">
          {liveAnswers.filter((a) => a.status === 'done').length} / {liveAnswers.length} voices in
        </div>
      </div>
      <div className="mt-4 space-y-3">
        {liveAnswers.map((a, i) => {
          const p = personas.find((x) => x.id === a.persona);
          const Icon = p ? (ICON_MAP[p.icon] || Sparkles) : Sparkles;
          return (
            <div
              key={`${a.persona}-${a.round}-${i}`}
              data-testid={`council-live-${a.persona}`}
              className={`rounded-md border p-3 transition-all ${
                a.status === 'done'
                  ? 'border-[#8B3FC5]/30 bg-white dark:bg-white/[0.02]'
                  : 'border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.03]'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-full border border-[#8B3FC5]/40 bg-black flex items-center justify-center shrink-0">
                  <Icon size={16} className="text-[#8B3FC5]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-sm font-bold">{p?.english_name || a.persona}</div>
                    {a.status === 'thinking' && (
                      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 dark:text-white/45">
                        <Loader2 size={10} className="animate-spin" /> thinking
                      </span>
                    )}
                    {a.status === 'done' && (
                      <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
                        <Check size={10} /> in
                      </span>
                    )}
                  </div>
                  {a.status === 'done' && (
                    <p className="mt-2 text-sm leading-relaxed text-slate-700 dark:text-white/80 whitespace-pre-wrap">{a.answer}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
